// import ErrorIcon from '@mui/icons-material/Error';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FaxOutlinedIcon from '@mui/icons-material/FaxOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import { FC, useCallback, useMemo, useState } from 'react';
import { CustomDialog } from 'src/components/dialogs/CustomDialog';
import { RoundedButton } from 'src/components/RoundedButton';
import { dataTestIds } from 'src/constants/data-test-ids';
import { stripFileExtension } from 'src/helpers/files.helper';
import { formatISOStringToDateAndTime } from 'src/helpers/formatDateTime';
import { PatientDocumentInfo } from 'src/hooks/useGetPatientDocs';
import { PatientVisitOption, usePatientVisitOptions } from 'src/hooks/usePatientVisitOptions';
import { FAX_PACKET_CODE, MEDICAL_RECORD_EXPORT_CODE } from 'utils/lib/types/data/paperwork/paperwork.constants';
import { isFaxableAttachment } from 'utils/lib/utils/file';

export enum DocumentTableActionType {
  ActionDownload = 'ActionDownload',
  ActionFax = 'ActionFax',
  ActionRename = 'ActionRename',
  ActionDelete = 'ActionDelete',
}

export type DocumentTableActions = {
  isActionAllowed: (documentId: string, actionType: DocumentTableActionType) => boolean;
  onDocumentDownload: (documentId: string) => Promise<void>;
  /** Opens the fax dialog for this document; faxing is a single-document action by design. */
  onDocumentFax: (documentId: string) => void;
  onDocumentRename: (documentId: string, newName: string) => Promise<void>;
  onDocumentDelete: (documentId: string) => Promise<void>;
};

const DocActionsCell: FC<{ docInfo: PatientDocumentInfo; actions: DocumentTableActions }> = ({ docInfo, actions }) => {
  const { isActionAllowed, onDocumentDownload, onDocumentFax, onDocumentRename, onDocumentDelete } = actions;
  const theme = useTheme();
  const lineColor = theme.palette.primary.main;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isRenameDialogOpen, setRenameDialogOpen] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>(stripFileExtension(docInfo.docName));
  const [renameLoading, setRenameLoading] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const hasMenuActions =
    isActionAllowed(docInfo.id, DocumentTableActionType.ActionRename) ||
    isActionAllowed(docInfo.id, DocumentTableActionType.ActionDelete);

  const openMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = (): void => {
    setAnchorEl(null);
  };

  const handleDocDownload = useCallback(async (): Promise<void> => {
    closeMenu();
    await onDocumentDownload(docInfo.id);
  }, [docInfo.id, onDocumentDownload]);

  const handleRenameDialogOpen = (): void => {
    closeMenu();
    setRenameDialogOpen(true);
  };

  const handleRenameDialogClose = (): void => {
    setRenameDialogOpen(false);
    setNewName(stripFileExtension(docInfo.docName));
  };

  const handleRenameSubmit = async (): Promise<void> => {
    setRenameLoading(true);

    try {
      await onDocumentRename(docInfo.id, newName.trim());
      setRenameDialogOpen(false);
    } catch {
      enqueueSnackbar(`Can't rename document. Try again later`, { variant: 'error' });
    } finally {
      setRenameLoading(false);
    }
  };

  const handleDeleteDialogOpen = (): void => {
    closeMenu();
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = (): void => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteSubmit = async (): Promise<void> => {
    setDeleteLoading(true);

    try {
      await onDocumentDelete(docInfo.id);
      setDeleteDialogOpen(false);
    } catch {
      enqueueSnackbar(`Can't delete document. Try again later`, { variant: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        {/* <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
          TODO
        </Typography>
        DownloadIcon
        <ErrorIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', color: lineColor }} /> */}

        {isActionAllowed(docInfo.id, DocumentTableActionType.ActionDownload) && (
          <IconButton aria-label="Download" onClick={handleDocDownload}>
            <DownloadIcon fontSize="small" sx={{ verticalAlign: 'middle', color: lineColor }} />
          </IconButton>
        )}

        {isActionAllowed(docInfo.id, DocumentTableActionType.ActionFax) && isDocumentFaxable(docInfo) && (
          <Tooltip title="Send Fax">
            <IconButton
              aria-label="Send Fax"
              onClick={() => onDocumentFax(docInfo.id)}
              data-testid={dataTestIds.patientDocsPage.faxDocumentButton(docInfo.id)}
            >
              <FaxOutlinedIcon fontSize="small" sx={{ verticalAlign: 'middle', color: lineColor }} />
            </IconButton>
          </Tooltip>
        )}

        {/* {isActionAllowed(docInfo.id, DocumentTableActionType.ActionDownload) && (
          <IconButton aria-label="Download" onClick={() => onDocumentDownload(docInfo.id)}>
            <DownloadIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', color: lineColor }} />
          </IconButton>
        )} */}
        {/* Both menu entries are gated, so hide the trigger rather than open an empty menu. */}
        {hasMenuActions && (
          <IconButton aria-label="More actions" onClick={openMenu}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {isActionAllowed(docInfo.id, DocumentTableActionType.ActionRename) && (
          <MenuItem onClick={handleRenameDialogOpen}>
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Rename document</ListItemText>
          </MenuItem>
        )}
        {isActionAllowed(docInfo.id, DocumentTableActionType.ActionDelete) && (
          <MenuItem onClick={handleDeleteDialogOpen}>
            <ListItemIcon>
              <DeleteIcon color="error" />
            </ListItemIcon>
            <ListItemText>Delete document</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <CustomDialog
        open={isRenameDialogOpen}
        handleClose={handleRenameDialogClose}
        title="Rename Document"
        description={
          <Box sx={{ width: '436px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              autoFocus
              label="Document name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              error={!newName.trim()}
              helperText={newName.trim().length === 0 ? 'Document name cannot be empty.' : ''}
              fullWidth
              required
              sx={{ mt: 1 }}
            />
          </Box>
        }
        actions={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <RoundedButton onClick={handleRenameDialogClose}>Cancel</RoundedButton>
            <RoundedButton
              variant="contained"
              onClick={handleRenameSubmit}
              loading={renameLoading}
              disabled={!newName.trim()}
            >
              Save
            </RoundedButton>
          </Box>
        }
      />

      <CustomDialog
        open={isDeleteDialogOpen}
        handleClose={handleDeleteDialogClose}
        title="Delete Document"
        description={
          <Box sx={{ width: '436px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography>{`"${docInfo.docName}" will be permanently deleted. This action cannot be undone.`}</Typography>
          </Box>
        }
        actions={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <RoundedButton onClick={handleDeleteDialogClose}>Cancel</RoundedButton>
            <RoundedButton variant="contained" onClick={handleDeleteSubmit} loading={deleteLoading}>
              Delete
            </RoundedButton>
          </Box>
        }
      />
    </>
  );
};

/**
 * Only documents a fax can actually carry get the action — offering it for a medical-record archive
 * or another unsupported format would fail later with "nothing faxable".
 */
const isDocumentFaxable = (docInfo: PatientDocumentInfo): boolean => {
  if (docInfo.typeCodes?.some((code) => code === FAX_PACKET_CODE || code === MEDICAL_RECORD_EXPORT_CODE)) return false;
  // Judged on the stored attachment, exactly as the server judges it when the fax is built — a
  // display title is not a MIME type, and offering an action the server will drop is worse than
  // hiding one it would have accepted.
  return (docInfo.attachments ?? []).some((attachment) =>
    isFaxableAttachment({ url: attachment.z3Url, contentType: attachment.contentType })
  );
};

/**
 * The visit a document was filed against: appointment date/time on top, visit id below. Documents
 * uploaded before visit association existed (and patient-level uploads) have no visit, so they
 * render a dash.
 */
const VisitCell: FC<{ visit?: PatientVisitOption }> = ({ visit }) => {
  const theme = useTheme();

  if (!visit) return <>-</>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
      <Typography variant="body2">{visit.dateTime ? formatISOStringToDateAndTime(visit.dateTime) : '-'}</Typography>
      {visit.appointmentId && (
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {visit.appointmentId}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Resolves a document's visit from either linkage: EHR uploads carry an encounter, intake paperwork
 * only an appointment. Encounter is tried first — it is the more specific of the two.
 */
const resolveDocumentVisit = (
  doc: PatientDocumentInfo,
  visitsByEncounterId: Map<string, PatientVisitOption>,
  visitsByAppointmentId: Map<string, PatientVisitOption>
): PatientVisitOption | undefined =>
  (doc.encounterId ? visitsByEncounterId.get(doc.encounterId) : undefined) ??
  (doc.appointmentId ? visitsByAppointmentId.get(doc.appointmentId) : undefined);

const configureTableColumns = (
  actions: DocumentTableActions,
  visitsByEncounterId: Map<string, PatientVisitOption>,
  visitsByAppointmentId: Map<string, PatientVisitOption>
): GridColDef<PatientDocumentInfo>[] => {
  return [
    {
      sortable: false,
      field: 'docName',
      headerName: 'Doc Name',
      width: 400,
      renderCell: ({ row: { docName } }) => docName,
    },
    {
      sortable: true,
      field: 'encounterId',
      headerName: 'Visit',
      width: 200,
      // Sort by the visit's date/time rather than a raw resource id, which is meaningless to order.
      valueGetter: ({ row }) => resolveDocumentVisit(row, visitsByEncounterId, visitsByAppointmentId)?.dateTime ?? '',
      renderCell: ({ row }) => (
        <VisitCell visit={resolveDocumentVisit(row, visitsByEncounterId, visitsByAppointmentId)} />
      ),
    },
    {
      sortComparator: (a, b) => {
        console.log(`[When added] sortComparator() a=${a} :: b=${b}`);
        const createdA = DateTime.fromISO(a ?? '');
        const createdB = DateTime.fromISO(b ?? '');
        return createdA.diff(createdB).milliseconds;
      },
      sortable: true,
      field: 'whenAddedDate',
      headerName: 'When added',
      width: 150,
      renderCell: ({ row: { whenAddedDate } }) => (whenAddedDate ? formatISOStringToDateAndTime(whenAddedDate) : '-'),
    },
    {
      sortable: true,
      field: 'whoAdded',
      headerName: 'Who added',
      width: 150,
      renderCell: ({ row: { whoAdded } }) => whoAdded ?? '-',
    },
    {
      sortable: false,
      field: 'actions',
      headerName: 'Action',
      width: 150,
      renderCell: ({ row }) => <DocActionsCell docInfo={row} actions={actions} />,
    },
  ];
};

export type PatientDocumentsExplorerTableProps = {
  isLoadingDocs: boolean;
  documents?: PatientDocumentInfo[];
  documentTableActions: DocumentTableActions;
  /** Used to resolve each document's visit into a date/time + visit id for the "Visit" column. */
  patientId: string;
};

export const PatientDocumentsExplorerTable: FC<PatientDocumentsExplorerTableProps> = (props) => {
  const { isLoadingDocs, documents, documentTableActions, patientId } = props;

  const { visitsByEncounterId, visitsByAppointmentId } = usePatientVisitOptions(patientId);

  const filteredDocs = documents ?? [];

  const tableColumns = useMemo(() => {
    return configureTableColumns(documentTableActions, visitsByEncounterId, visitsByAppointmentId);
  }, [documentTableActions, visitsByEncounterId, visitsByAppointmentId]);

  return (
    <DataGridPro
      rows={filteredDocs}
      columns={tableColumns}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 10,
          },
        },
        sorting: {
          sortModel: [{ field: 'whenAddedDate', sort: 'desc' }],
        },
      }}
      autoHeight
      loading={isLoadingDocs}
      pagination
      disableColumnMenu
      pageSizeOptions={[10]}
      disableRowSelectionOnClick
      sx={{
        border: 0,
        '.MuiDataGrid-columnHeaderTitle': {
          fontWeight: 500,
        },
      }}
    />
  );
};
